'use client';

import React, { useEffect, useRef, useCallback } from 'react';
import styles from './ScrollProgress.module.css';

/**
 * ScrollProgress Component
 * Shows a progress bar indicating how far the user has scrolled down the page
 * Uses ref-based DOM updates to avoid React re-renders on every scroll event
 */
export function ScrollProgress() {
  const barRef = useRef<HTMLDivElement>(null);
  const rafId = useRef<number | null>(null);

  const updateScrollProgress = useCallback(() => {
    if (!barRef.current) return;

    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY;

    const scrollableHeight = documentHeight - windowHeight;
    const progress = scrollableHeight > 0 ? (scrollTop / scrollableHeight) * 100 : 0;

    barRef.current.style.width = `${Math.min(progress, 100)}%`;
  }, []);

  useEffect(() => {
    updateScrollProgress();

    const handleEvent = () => {
      if (rafId.current === null) {
        rafId.current = requestAnimationFrame(() => {
          updateScrollProgress();
          rafId.current = null;
        });
      }
    };

    window.addEventListener('scroll', handleEvent, { passive: true });
    window.addEventListener('resize', handleEvent, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleEvent);
      window.removeEventListener('resize', handleEvent);
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [updateScrollProgress]);

  return (
    <div className={styles.scrollProgressContainer}>
      <div
        ref={barRef}
        className={styles.scrollProgressBar}
        style={{ width: '0%' }}
      />
    </div>
  );
}
